import { Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, NavigationSkipped, ParamMap, Router } from '@angular/router';
import { Subscription, firstValueFrom } from 'rxjs';
import { UserService } from 'app/services/user.service';
import { clientZoneDate } from 'app/shared/utils/client-zone-date';
import {
	AgentCredits,
	AgentCreditWarning,
	AgentModelOption,
	AgentSaveChatPayload,
	AgentSavedChatDetail,
	AgentService,
	AgentSseEvent,
	AgentTurnAttachment,
	CodeExecStatus,
	agentCreditWarning,
} from 'app/services/AIAgent/agent.service';

// Phase 7 (EN-3700) — the in-portal Agent's full-page surface. Distinct from the slide-in RAG
// documentation assistant (which is never retired) and from the HubSpot support widget. Reached from
// its own nav-bar entry, gated by FeatureFlags.PortalAgent. Streams the Anthropic-direct MCP-client
// loop over SSE and renders text deltas + tool activity live, plus a non-error cap-hit state for the
// budget 402. Token counts and dollar costs are deliberately NOT displayed (Andrew, 2026-06-10:
// no "$0.89" moments in client demos); usage is still metered and persisted server-side
// (AgentTokenUsages) and the 402 budget gate still applies.

// EN-5079: the addresses of a new chat and of a saved chat (claire/c/:conversationId).
export const NEW_CHAT_PATH = '/claire/new';
export const SAVED_CHAT_PATH = '/claire/c';
// The addresses that open a fresh conversation: the submenu's New chat and the plain Claire link.
const NEW_CHAT_URLS = [NEW_CHAT_PATH, '/claire'];

interface AgentMessage {
	role: 'user' | 'assistant';
	content: string;
	tools: string[];
	timestamp: Date;
	pending?: boolean;
	// Local UI-only messages (e.g. the greeting) are not replayed to the model as prior context.
	local?: boolean;
	// Elapsed wall time for the per-round working indicator.
	startedAt?: number;
	elapsedMs?: number;
	// Files the user attached to this turn (name + type only; the bytes are not retained for display).
	attachments?: { fileName: string; mediaType: string }[];
	// Server-side code-execution status for this turn, surfaced as a live chip while the agent runs code
	// (e.g. building a workbook): 'running' while in flight, 'completed'/'failed' once the result lands.
	// Undefined when the turn used no code execution.
	codeExec?: CodeExecStatus;
	// Files the Agent authored this turn (code-execution outputs), as short-lived signed download links.
	files?: { fileName: string; downloadUrl: string; expiresUtc: string }[];
	// EN-5079: on a reopened saved chat, the names of the files Claire produced. The download links
	// expired an hour after the turn, so only the names are shown.
	savedFiles?: string[];
	// A calm, non-error notice shown under the answer when the turn ended on a protective stop reason
	// (e.g. the per-request token ceiling truncated the response), so the user understands why the
	// reply stopped instead of seeing a silent cut-off. EN-4014.
	notice?: string;
	// Memoized render of `content`, maintained by renderContent (EN-4433). Held on the message rather
	// than in a side map so it is discarded with the message and cannot outlive it.
	renderedFrom?: string;
	renderedHtml?: SafeHtml;
}

@Component({
	selector: 'app-ai-agent',
	templateUrl: './ai-agent.component.html',
	styleUrls: ['./ai-agent.component.css'],
	imports: [FormsModule, CommonModule],
})
export class AiAgentComponent implements OnInit, OnDestroy {
	@ViewChild('chatScroll') private chatScrollRef!: ElementRef;

	messages: AgentMessage[] = [];
	userInput = '';
	charLimit = 4000;
	isStreaming = false;
	userName = '';

	// Model picker (EN-3700). Populated from the server; selection persists across New chat and is sent
	// with each turn. Empty list => the server uses its configured default and the dropdown is hidden.
	models: AgentModelOption[] = [];
	selectedModel = '';

	// File attachments staged for the next turn. Mirrors the server-side limits in AIChatbotController.
	readonly maxAttachments = 5;
	readonly maxAttachmentBytes = 10 * 1024 * 1024;
	readonly acceptedFileTypes = 'application/pdf,image/png,image/jpeg,image/gif,image/webp';
	pendingAttachments: AgentTurnAttachment[] = [];
	attachmentError: string | null = null;

	capHit: { error: string; reason: string } | null = null;

	// EN-4740: the ledger's credit chip. Null while it has not loaded, or when the endpoint 404s
	// because the feature is off for this client, in which case no chip is drawn at all.
	credits: AgentCredits | null = null;

	// Monotonic, so a credit response that arrives out of order can be recognised and dropped.
	private creditRequest = 0;
	errorMessage: string | null = null;

	// EN-5079: whether this conversation is a saved chat. Once saved, the server appends every later
	// turn itself, so the button only ever saves once per conversation.
	isSaved = false;
	isSaving = false;
	// True while a saved chat is being fetched; sending waits for it so a turn never runs against a
	// conversation that is about to be replaced.
	isLoadingChat = false;

	// Bumped whenever the conversation on screen is replaced (New chat, opening a saved chat, leaving
	// the page). A save or a load that resolves after that belongs to a conversation that is gone, so
	// its result is dropped instead of marking or overwriting the one now on screen.
	private conversationGeneration = 0;

	private readonly route = inject(ActivatedRoute);
	private readonly location = inject(Location);
	private readonly router = inject(Router);

	private conversationId = '';
	private streamSub?: Subscription;
	private routeSub?: Subscription;
	private skippedNavigationSub?: Subscription;
	private liveMsg?: AgentMessage;
	// Set after a tool round so the next streamed text starts a new paragraph. Without it, the model's
	// narration before a tool call and its continuation after run together (e.g. "...the deck.Now I...").
	private pendingParagraphBreak = false;
	private elapsedTimer?: ReturnType<typeof setInterval>;

	constructor(
		private readonly agentService: AgentService,
		private readonly userService: UserService,
		private readonly zone: NgZone,
		private readonly sanitizer: DomSanitizer
	) {
		this.userName = this.userService.currentUserValue?.user?.fullName || 'User';
	}

	// Renders the assistant's markdown to safe HTML, memoized per message. XSS-safe by construction:
	// every text segment is HTML-escaped before any tags are added, and only a known set of block/inline
	// tags is emitted; links are restricted to http(s) URLs opened in a new tab. Supports GFM pipe
	// tables, headings, ordered and unordered lists, fenced and inline code, blockquotes, bold/italic,
	// and links. Paragraphs join soft line breaks with a space so prose keeps correct spacing (a blank
	// line starts a new paragraph).
	//
	// The memoization is load-bearing, not an optimization (EN-4433). bypassSecurityTrustHtml returns a
	// NEW SafeHtml object on every call and [innerHTML] compares by reference, so calling this straight
	// from the template left the binding dirty on every change-detection tick. Angular then rebuilt the
	// rendered answer's DOM each tick (the bound span only; the tool chips and notices are separate
	// views and never churned), and a tick landing between mousedown and mouseup detached the anchor
	// under the pointer, so links in Agent answers never opened (hover styling still worked, which is
	// why it read as a dead link rather than an error). Caching also removes the O(messages x length)
	// full-conversation re-parse per tick, a client Rule 5 violation.
	//
	// Takes the message, not the text, so the cache can live with the value it describes. Do NOT "fix"
	// this class of bug with OnPush (it breaks the in-place mutation that streams deltas) or with a
	// bubble-phase click delegate (it fires after the tick has already destroyed the node).
	renderContent(msg: AgentMessage): SafeHtml {
		const text = msg.content || '';
		if (msg.renderedFrom !== text) {
			msg.renderedFrom = text;
			msg.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(this.renderMarkdown(text));
		}
		return msg.renderedHtml;
	}

	private renderMarkdown(src: string): string {
		const lines = src.replace(/\r\n?/g, '\n').split('\n');
		const out: string[] = [];
		const isBlank = (s: string) => s.trim().length === 0;
		// A GFM table separator row, e.g. "| --- | :--: |".
		const isTableSep = (s: string) => /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)+\|?\s*$/.test(s);
		const isHeading = (s: string) => /^#{1,6}\s+/.test(s);
		const isQuote = (s: string) => /^\s*>\s?/.test(s);
		const isUl = (s: string) => /^\s*[-*+]\s+/.test(s);
		const isOl = (s: string) => /^\s*\d+\.\s+/.test(s);
		const isFence = (s: string) => /^\s*```/.test(s);
		const isTableStart = (idx: number) =>
			lines[idx].includes('|') && idx + 1 < lines.length && isTableSep(lines[idx + 1]);

		let i = 0;
		while (i < lines.length) {
			const line = lines[i];

			if (isBlank(line)) {
				i++;
				continue;
			}

			// Fenced code block: contents are escaped, never inline-parsed.
			if (isFence(line)) {
				const body: string[] = [];
				i++;
				while (i < lines.length && !isFence(lines[i])) {
					body.push(lines[i]);
					i++;
				}
				if (i < lines.length) i++; // consume closing fence
				out.push('<pre class="md-pre"><code>' + this.escapeHtml(body.join('\n')) + '</code></pre>');
				continue;
			}

			// GFM table: a row with '|' followed by a separator row.
			if (isTableStart(i)) {
				const header = line;
				const rows: string[] = [];
				i += 2;
				while (i < lines.length && lines[i].includes('|') && !isBlank(lines[i])) {
					rows.push(lines[i]);
					i++;
				}
				out.push(this.renderTable(header, rows));
				continue;
			}

			// Heading
			const heading = line.match(/^(#{1,6})\s+(.*)$/);
			if (heading) {
				const level = Math.min(heading[1].length, 6);
				out.push(`<h${level}>` + this.renderInline(heading[2].trim()) + `</h${level}>`);
				i++;
				continue;
			}

			// Blockquote
			if (isQuote(line)) {
				const quote: string[] = [];
				while (i < lines.length && isQuote(lines[i])) {
					quote.push(lines[i].replace(/^\s*>\s?/, ''));
					i++;
				}
				out.push('<blockquote>' + this.renderInline(quote.join(' ')) + '</blockquote>');
				continue;
			}

			// Unordered list
			if (isUl(line)) {
				const items: string[] = [];
				while (i < lines.length && isUl(lines[i])) {
					items.push('<li>' + this.renderInline(lines[i].replace(/^\s*[-*+]\s+/, '')) + '</li>');
					i++;
				}
				out.push('<ul>' + items.join('') + '</ul>');
				continue;
			}

			// Ordered list
			if (isOl(line)) {
				const items: string[] = [];
				while (i < lines.length && isOl(lines[i])) {
					items.push('<li>' + this.renderInline(lines[i].replace(/^\s*\d+\.\s+/, '')) + '</li>');
					i++;
				}
				out.push('<ol>' + items.join('') + '</ol>');
				continue;
			}

			// Paragraph: consecutive plain lines joined with a space (soft breaks become spaces, so a
			// sentence wrapped across lines keeps its spacing after punctuation).
			const para: string[] = [];
			while (
				i < lines.length &&
				!isBlank(lines[i]) &&
				!isFence(lines[i]) &&
				!isHeading(lines[i]) &&
				!isQuote(lines[i]) &&
				!isUl(lines[i]) &&
				!isOl(lines[i]) &&
				!isTableStart(i)
			) {
				para.push(lines[i].trim());
				i++;
			}
			if (para.length) {
				out.push('<p>' + this.renderInline(para.join(' ')) + '</p>');
			}
		}

		return out.join('');
	}

	private renderTable(headerLine: string, rowLines: string[]): string {
		const splitCells = (s: string) => {
			let t = s.trim();
			if (t.startsWith('|')) t = t.slice(1);
			if (t.endsWith('|')) t = t.slice(0, -1);
			return t.split('|').map((c) => c.trim());
		};
		const headers = splitCells(headerLine);
		const thead =
			'<thead><tr>' + headers.map((h) => '<th>' + this.renderInline(h) + '</th>').join('') + '</tr></thead>';
		const tbody =
			'<tbody>' +
			rowLines
				.map((r) => {
					const cells = splitCells(r);
					// Align each row to the header width so a ragged row cannot break the table layout.
					const tds = headers.map((_h, idx) => '<td>' + this.renderInline(cells[idx] ?? '') + '</td>').join('');
					return '<tr>' + tds + '</tr>';
				})
				.join('') +
			'</tbody>';
		return '<div class="md-table-wrap"><table class="md-table">' + thead + tbody + '</table></div>';
	}

	// Inline formatting for one logical line. Escapes HTML first, then injects only known-safe inline
	// tags. Inline code is handled first so its contents are not re-formatted.
	private renderInline(text: string): string {
		let s = this.escapeHtml(text);

		// Inline code `code`
		s = s.replace(/`([^`]+)`/g, (_m, code) => '<code>' + code + '</code>');

		// Markdown links [label](http(s)://url)
		s = s.replace(
			/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
			(_m, label, url) => `<a href="${this.cleanUrl(url)}" target="_blank" rel="noopener noreferrer">${label}</a>`
		);

		// Bare URLs not already inside an anchor href or following a tag close.
		s = s.replace(
			/(^|[^"=>])(https?:\/\/[^\s<]+)/g,
			(_m, pre, url) => `${pre}<a href="${this.cleanUrl(url)}" target="_blank" rel="noopener noreferrer">${url}</a>`
		);

		// Bold **text**
		s = s.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

		// Italic *text* and _text_ (avoid intra-word underscores and the ** already consumed).
		s = s.replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1<em>$2</em>');
		s = s.replace(/(^|[\s(])_([^_\s][^_]*?)_(?=[\s).,!?:;]|$)/g, '$1<em>$2</em>');

		return s;
	}

	private escapeHtml(value: string): string {
		return value
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	private cleanUrl(url: string): string {
		// The regex already constrained the scheme to http(s); drop any escaped quotes that could break out.
		return url.replace(/&quot;/g, '').replace(/&#39;/g, '');
	}

	// Read live from the user service each time rather than caching: the active client is the one the
	// portal currently has selected, and binding to it per turn keeps the agent correct even if client
	// switching ever stops doing a full page reload (today it reloads, which also recreates this
	// component). The backend re-mints the MCP token from this clientId on every turn.
	private get clientId(): string {
		return this.userService.currentUserValue?.user?.clientId ?? '';
	}

	async ngOnInit(): Promise<void> {
		this.startNewConversation();
		// claire/c/:conversationId reopens a saved chat. The same component instance stays mounted when
		// the user opens a second saved chat from the first, so the parameter is followed, not read once.
		this.routeSub = this.route.paramMap.subscribe((params) => void this.onRouteChange(params));
		// The sidebar's New chat (and Andi's Claire link) point at the page that is already open, so the
		// router reuses this component and nothing on it changes on its own. Two cases reach here:
		// - the address is already the new-chat page: the router skips the click (NavigationSkipped);
		// - a save rewrote the address to the chat's own one: the router sees the browser address
		//   differ and navigates (NavigationEnd), but to the same route, so the component is kept.
		// Either way the user asked for a new chat. Without this, the saved chat stays on screen and
		// the next question is appended to it.
		this.skippedNavigationSub = this.router.events.subscribe((event) => {
			const url =
				event instanceof NavigationEnd
					? event.urlAfterRedirects
					: event instanceof NavigationSkipped
					? event.url
					: null;
			if (url && NEW_CHAT_URLS.includes(url) && this.messages.length > 0) {
				this.newChat();
			}
		});
		await this.loadModels();
		await this.loadCredits();
	}

	private async onRouteChange(params: ParamMap): Promise<void> {
		const chatId = params.get('conversationId');
		// Saving rewrites the URL to this chat's address; that is not a request to reload it.
		if (!chatId || (chatId === this.conversationId && this.isSaved)) return;
		await this.openSavedChat(chatId);
	}

	// Loads a saved chat and continues it: the stored turns become the messages, so the next turn's
	// priorTurns carry them exactly as if the conversation had never left the page.
	private async openSavedChat(chatId: string): Promise<void> {
		this.streamSub?.unsubscribe();
		this.stopElapsedTimer();
		this.isStreaming = false;
		this.startNewConversation();
		const generation = this.conversationGeneration;
		this.isLoadingChat = true;

		let chat: AgentSavedChatDetail;
		try {
			chat = await firstValueFrom(this.agentService.getSavedChat(this.clientId, chatId));
		} catch {
			if (generation !== this.conversationGeneration) return;
			this.isLoadingChat = false;
			this.errorMessage = 'This chat is not available. It may have been deleted.';
			return;
		}

		if (generation !== this.conversationGeneration) return;
		this.isLoadingChat = false;
		this.conversationId = chat.id;
		this.isSaved = true;
		this.messages = chat.messages.map((m) => ({
			role: m.role,
			content: m.text ?? '',
			tools: [],
			timestamp: new Date(m.createdUtc),
			attachments: m.attachments?.length ? m.attachments : undefined,
			savedFiles: m.files?.length ? m.files : undefined,
		}));
		this.scrollToBottom();
	}

	// Save is offered once a turn has finished, and only once per conversation.
	get canSave(): boolean {
		return (
			!this.isSaved &&
			!this.isSaving &&
			!this.isStreaming &&
			this.messages.some((m) => m.role === 'assistant' && !m.pending && !m.local)
		);
	}

	async saveChat(): Promise<void> {
		if (!this.canSave) return;
		this.isSaving = true;
		this.errorMessage = null;
		const generation = this.conversationGeneration;

		const payload: AgentSaveChatPayload = {
			conversationId: this.conversationId,
			messages: this.messages
				.filter((m) => !m.local && (m.content || m.attachments?.length || m.files?.length || m.savedFiles?.length))
				.map((m) => ({
					role: m.role,
					text: m.content,
					attachments: m.attachments,
					files: m.files?.map((f) => f.fileName) ?? m.savedFiles,
				})),
		};

		try {
			await firstValueFrom(this.agentService.saveChat(this.clientId, payload));
			// The user moved on while the save was in flight. That conversation is saved on the server;
			// the one on screen now is not, so nothing here may claim it is.
			if (generation !== this.conversationGeneration) return;
			this.isSaved = true;
			// Give the chat its own address so a refresh or a bookmark reopens it. Rewritten in place
			// rather than navigated: claire and claire/c/:id are separate routes, and a navigation would
			// rebuild this component and drop the model choice and credit chip mid-conversation.
			this.location.replaceState(`${SAVED_CHAT_PATH}/${this.conversationId}`);
		} catch (error) {
			if (generation !== this.conversationGeneration) return;
			// The interceptor hands back the server's sentence (e.g. a chat over the message limit) as a
			// string; anything else falls back to a generic message.
			this.errorMessage = typeof error === 'string' && error ? error : 'Could not save this chat. Please try again.';
		} finally {
			if (generation === this.conversationGeneration) this.isSaving = false;
		}
	}

	// Which warning the chip shows: none, the 80 percent warning, the 95 percent one, or blocked.
	get creditWarning(): AgentCreditWarning {
		return agentCreditWarning(this.credits);
	}

	// The chip is drawn only when there is something to say: a limit to count against, a refusal the
	// product will actually act on, or staff status. A client with credits and no limit sees no
	// chrome at all.
	//
	// Note what that leaves out, deliberately. While enforcement is off, agentCreditWarning reports
	// 'none' for a refusal, so a client whose BALANCE is exhausted but who has no limit sees nothing
	// at all. That is the parity week working as intended: nobody is being refused yet, and the
	// ledger those figures come from is not trusted enough to tell a client they are out of credits.
	// The low-balance signal for that week is the Slack alert (EN-4745), not this chip. When Enforce
	// goes on, the same state reports 'blocked' and the chip appears.
	get showCreditChip(): boolean {
		if (!this.credits) return false;
		return this.credits.percentUsed !== null || this.creditWarning !== 'none' || this.credits.isAclarianStaff;
	}

	// The client's calendar day, not the browser's: a reset at midnight in New York is still the
	// previous evening anywhere west of it. "does not reset" for a Lifetime limit, which never rolls.
	get creditResetLabel(): string {
		if (!this.credits?.limitCap && !this.credits?.limitFiguresHidden) return '';
		return this.credits.resetUtc
			? `resets ${clientZoneDate(this.credits.resetUtc, this.credits.timeZoneId)}`
			: 'does not reset';
	}

	// First name only, for the empty-state greeting ("How can I help, Jonathas?").
	get firstName(): string {
		return (this.userName || '').trim().split(/\s+/)[0] || 'there';
	}

	// Loads the selectable models and the default. A failure (feature off / not configured) leaves the
	// dropdown hidden; turns then run on the server's configured default.
	private async loadModels(): Promise<void> {
		try {
			const list = await firstValueFrom(this.agentService.getModels(this.clientId));
			this.models = list?.models ?? [];
			const def = list?.defaultModel ?? '';
			this.selectedModel = this.models.some((m) => m.id === def) ? def : this.models[0]?.id ?? def;
		} catch {
			this.models = [];
		}
	}

	// The credit chip draws a GATE answer, and the gate is model-sensitive: the account carries an
	// allowed-model list and the price list weighs each model differently, so the same user can be
	// allowed on one model and blocked on another. Re-read rather than adjusted locally, the same
	// rule the post-turn refresh follows.
	async onModelChange(modelId: string): Promise<void> {
		this.selectedModel = modelId;
		await this.loadCredits();
	}

	// Loads the credit chip. A failure (feature off, not configured, or the client not seeded) leaves
	// the chip hidden rather than showing a zero balance, which would read as "you are out of
	// credits" to a client who simply is not on the ledger yet.
	private async loadCredits(): Promise<void> {
		// Sequenced, because these overlap. Switching models twice in quick succession leaves two
		// reads in flight, and nothing makes the responses arrive in the order they were asked for:
		// the first model's answer landing last would leave the chip showing that model's cap and
		// reason while a different model is selected, and it would stay wrong until some later turn
		// happened to refresh it. Only the newest request may write.
		const request = ++this.creditRequest;

		try {
			const credits = await firstValueFrom(this.agentService.getCredits(this.clientId, this.selectedModel));
			if (request === this.creditRequest) this.credits = credits;
		} catch {
			if (request === this.creditRequest) this.credits = null;
		}
	}

	// Empty-state suggestion chip: prefill the composer so the user can edit before sending.
	useSuggestion(text: string): void {
		this.userInput = text;
	}

	ngOnDestroy(): void {
		this.streamSub?.unsubscribe();
		this.conversationGeneration++;
		this.routeSub?.unsubscribe();
		this.skippedNavigationSub?.unsubscribe();
		this.stopElapsedTimer();
	}

	get charCount(): string {
		return `${this.userInput.length} / ${this.charLimit}`;
	}

	newChat(): void {
		this.streamSub?.unsubscribe();
		this.stopElapsedTimer();
		this.isStreaming = false;
		this.startNewConversation();
		// Leave a saved chat's address, so a refresh does not bring the old chat back.
		if (this.location.path().startsWith(`${SAVED_CHAT_PATH}/`)) {
			this.location.replaceState(NEW_CHAT_PATH);
		}
	}

	private startNewConversation(): void {
		this.messages = [];
		this.capHit = null;
		this.errorMessage = null;
		this.pendingAttachments = [];
		this.attachmentError = null;
		this.liveMsg = undefined;
		this.conversationGeneration++;
		this.isSaved = false;
		this.isSaving = false;
		this.isLoadingChat = false;
		this.conversationId = this.generateConversationId();
		// No greeting message is pushed: an empty conversation renders the centered empty-state
		// (greeting + suggestions) in the template, Claude-Desktop style. The first user turn replaces it.
	}

	sendMessage(): void {
		const typed = this.userInput.trim();
		if ((!typed && this.pendingAttachments.length === 0) || this.isStreaming || this.isLoadingChat) return;

		this.capHit = null;
		this.errorMessage = null;

		// Take the staged attachments for this turn and clear them so they are not resent next turn.
		const attachments = this.pendingAttachments;
		this.pendingAttachments = [];
		this.attachmentError = null;

		// When the user attaches a file without typing, give the model a default instruction so the
		// first content block is never an empty text block.
		const input = typed || 'Please review the attached file(s) and tell me what you can do with them.';

		const priorTurns = this.messages
			.filter((m) => !m.local && m.content)
			.map((m) => ({ role: m.role, text: m.content }));

		this.messages.push({
			role: 'user',
			content: input,
			tools: [],
			timestamp: new Date(),
			attachments: attachments.map((a) => ({ fileName: a.fileName, mediaType: a.mediaType })),
		});
		this.userInput = '';

		this.pendingParagraphBreak = false;
		this.liveMsg = {
			role: 'assistant',
			content: '',
			tools: [],
			timestamp: new Date(),
			pending: true,
			startedAt: performance.now(),
			elapsedMs: 0,
		};
		this.messages.push(this.liveMsg);
		this.isStreaming = true;
		this.startElapsedTimer();
		this.scrollToBottom();

		this.streamSub = this.agentService
			.streamTurn(this.clientId, {
				message: input,
				conversationId: this.conversationId,
				priorTurns,
				attachments: attachments.length ? attachments : undefined,
				model: this.selectedModel || undefined,
			})
			.subscribe({
				next: (evt) => this.zone.run(() => this.handleEvent(evt)),
				error: () =>
					this.zone.run(() => {
						this.errorMessage = 'The connection to Claire was interrupted. Please try again.';
						this.finishTurn();
					}),
				complete: () => this.zone.run(() => this.finishTurn()),
			});
	}

	// Reads selected files into base64 and stages them for the next turn. Enforces the same count/size
	// limits as the server for a friendly message; the server re-validates authoritatively.
	async onFilesSelected(event: Event): Promise<void> {
		const inputEl = event.target as HTMLInputElement;
		const files = inputEl.files;
		if (!files || files.length === 0) return;
		this.attachmentError = null;

		for (let i = 0; i < files.length; i++) {
			const file = files[i];
			if (this.pendingAttachments.length >= this.maxAttachments) {
				this.attachmentError = `You can attach up to ${this.maxAttachments} files.`;
				break;
			}
			if (file.size > this.maxAttachmentBytes) {
				this.attachmentError = `"${file.name}" is too large (max ${this.maxAttachmentBytes / (1024 * 1024)} MB).`;
				continue;
			}
			try {
				const data = await this.readAsBase64(file);
				this.zone.run(() => {
					this.pendingAttachments.push({
						fileName: file.name,
						mediaType: file.type || 'application/octet-stream',
						data,
					});
				});
			} catch {
				this.attachmentError = `Could not read "${file.name}".`;
			}
		}

		// Reset the input so selecting the same file again re-fires the change event.
		inputEl.value = '';
	}

	removeAttachment(index: number): void {
		this.pendingAttachments.splice(index, 1);
	}

	private readAsBase64(file: File): Promise<string> {
		return new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				// readAsDataURL yields "data:<mime>;base64,<data>"; strip the prefix to send raw base64.
				const result = reader.result as string;
				const comma = result.indexOf(',');
				resolve(comma >= 0 ? result.slice(comma + 1) : result);
			};
			reader.onerror = () => reject(reader.error);
			reader.readAsDataURL(file);
		});
	}

	// Maps a protective terminal stop reason to a calm, user-facing notice on the current message.
	// "max_tokens" now only fires at the model's own maximum output for a single reply (the
	// per-request ceiling is unlimited org-wide since 2026-08, so there is no administrator limit
	// to raise); "max_iterations" is the tool-round cap. Other reasons (end_turn, stop_sequence,
	// tool_use, error) leave no notice.
	private applyTerminalNotice(reason: string): void {
		if (!this.liveMsg) return;
		switch (reason) {
			case 'max_tokens':
				this.liveMsg.notice =
					'This response reached the maximum output size the AI model can produce in a single reply, so it was cut short. Try narrowing the request (for example a shorter date range or fewer accounts), or breaking it into smaller steps.';
				break;
			case 'max_iterations':
				this.liveMsg.notice =
					'The Agent reached the maximum number of steps for a single request and stopped. Try breaking this into smaller requests.';
				break;
		}
	}

	private handleEvent(evt: AgentSseEvent): void {
		switch (evt.type) {
			case 'text':
				if (this.liveMsg) {
					// Text resuming after a tool round is a fresh thought; separate it with a blank line so
					// the pre-tool and post-tool narration don't run together (e.g. "...the deck.Now I...").
					if (this.pendingParagraphBreak && this.liveMsg.content && !/\n\s*$/.test(this.liveMsg.content)) {
						this.liveMsg.content += '\n\n';
					}
					this.pendingParagraphBreak = false;
					this.liveMsg.content += evt.text;
				}
				this.scrollToBottom();
				break;
			case 'tool_call':
				if (this.liveMsg && evt.name && !this.liveMsg.tools.includes(evt.name)) {
					this.liveMsg.tools.push(evt.name);
				}
				// Next text block (after the tool result) should begin a new paragraph.
				this.pendingParagraphBreak = true;
				this.scrollToBottom();
				break;
			case 'tool_result':
				// Tool activity is surfaced via the tool_call chips; results feed the next text delta.
				this.pendingParagraphBreak = true;
				break;
			case 'usage':
				// Token/cost figures are intentionally not surfaced in the UI (Andrew, 2026-06-10).
				// The server has already metered and persisted this frame's usage (AgentTokenUsages).
				break;
			case 'code_execution': {
				// Only the known statuses drive the chip. 'running' arrives when the server-side tool starts;
				// 'completed'/'failed' when it ends (and a later 'running' re-opens the chip for a second
				// code block). An unrecognized status (a backend regression or typo) is ignored rather than
				// shown as a misleading 'Analysis complete' chip, since SSE is an external boundary.
				const knownStatuses: readonly CodeExecStatus[] = ['running', 'completed', 'failed'];
				if (this.liveMsg && knownStatuses.includes(evt.status)) {
					this.liveMsg.codeExec = evt.status;
					// Narration after the run is a fresh thought, so flag a paragraph break like a tool round.
					if (evt.status !== 'running') {
						this.pendingParagraphBreak = true;
					}
				}
				this.scrollToBottom();
				break;
			}
			case 'file':
				if (this.liveMsg && evt.downloadUrl) {
					if (!this.liveMsg.files) this.liveMsg.files = [];
					this.liveMsg.files.push({
						fileName: evt.fileName,
						downloadUrl: evt.downloadUrl,
						expiresUtc: evt.expiresUtc,
					});
				}
				this.scrollToBottom();
				break;
			case 'done':
				// Terminal frame; the completion handler finalizes the UI. If the turn ended on a
				// protective stop reason, attach a calm notice so the user understands why the
				// reply stopped rather than seeing a silent truncation (EN-4014).
				this.applyTerminalNotice(evt.reason);
				break;
			case 'cap_hit':
				this.capHit = { error: evt.error, reason: evt.reason };
				this.dropEmptyLiveMessage();
				break;
			case 'error':
				this.errorMessage = evt.message;
				this.dropEmptyLiveMessage();
				break;
		}
	}

	private finishTurn(): void {
		this.isStreaming = false;
		this.stopElapsedTimer();
		if (this.liveMsg) {
			this.liveMsg.pending = false;
			// An assistant turn that produced no text and no tools (e.g. cap-hit/error) is noise; drop it.
			this.dropEmptyLiveMessage();
			this.liveMsg = undefined;
		}
		this.scrollToBottom();

		// The turn just spent credits, so the chip is stale. Re-read rather than decrementing
		// locally: the server prices the call, and a client-side guess would drift from the ledger
		// the moment a cache-heavy turn weighed differently from a fresh one.
		void this.loadCredits();
	}

	private startElapsedTimer(): void {
		this.stopElapsedTimer();
		this.elapsedTimer = setInterval(() => {
			if (this.liveMsg?.startedAt != null) {
				this.liveMsg.elapsedMs = performance.now() - this.liveMsg.startedAt;
			}
		}, 1000);
	}

	private stopElapsedTimer(): void {
		if (this.elapsedTimer) {
			clearInterval(this.elapsedTimer);
			this.elapsedTimer = undefined;
		}
		if (this.liveMsg?.startedAt != null) {
			this.liveMsg.elapsedMs = performance.now() - this.liveMsg.startedAt;
		}
	}

	// "1m 51s" / "12s" elapsed wall time.
	formatElapsed(ms: number | undefined): string {
		const totalSec = Math.max(0, Math.floor((ms || 0) / 1000));
		const m = Math.floor(totalSec / 60);
		const s = totalSec % 60;
		return m > 0 ? `${m}m ${s}s` : `${s}s`;
	}

	private dropEmptyLiveMessage(): void {
		if (this.liveMsg && !this.liveMsg.content && this.liveMsg.tools.length === 0 && !this.liveMsg.codeExec) {
			const idx = this.messages.indexOf(this.liveMsg);
			if (idx !== -1) this.messages.splice(idx, 1);
		}
	}

	private scrollToBottom(): void {
		setTimeout(() => {
			if (this.chatScrollRef) {
				this.chatScrollRef.nativeElement.scrollTop = this.chatScrollRef.nativeElement.scrollHeight;
			}
		}, 50);
	}

	// Always a v4 UUID: the conversation id becomes a saved chat's id, and the server accepts only a
	// GUID there (EN-5079). randomUUID is missing outside secure contexts, so the fallback builds one
	// from getRandomValues, which is available everywhere crypto is.
	private generateConversationId(): string {
		if (typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID();
		}
		const bytes = crypto.getRandomValues(new Uint8Array(16));
		// RFC 4122: the version nibble says 4 (random) and the variant bits say 10xx.
		bytes[6] = (bytes[6] & 0x0f) | 0x40;
		bytes[8] = (bytes[8] & 0x3f) | 0x80;
		const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
		return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
	}
}
